import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AgGrid from '../common/AgGrid';
import { GetJobListById, GetTalliesListById } from '../../services/accountService';
import Icon from '../common/Icon';
import { useNavigate, useLocation } from 'react-router-dom';

const Tallies = ({ clientId, clientName }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const [rowDataTallies, setRowDataTallies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRows, setExpandedRows] = useState(new Set());

  
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [paginatedData, setPaginatedData] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalParentRows, setTotalParentRows] = useState(0);


    useEffect(() => {
        if (clientId) {
            fetchJobList();
        } else {
            setError("Client ID is required");
            setLoading(false);
        }
    }, [clientId]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    }, []);

    const getDayOfWeek = useCallback((dateString) => {
        if (!dateString) return "";
        try {
            const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return "";
            return days[date.getDay()];
        } catch (e) {
            return "";
        }
    }, []);

    const formatNumberWithCommas = useCallback((value) => {
        if (!value) return "0";
        try {
            const num = parseFloat(value.toString().replace(/,/g, ''));
            if (isNaN(num)) return value;
            return num.toLocaleString('en-US');
        } catch (e) {
            return value;
        }
    }, []);

  
    const updatePaginatedData = useCallback(() => {
        if (!rowDataTallies || rowDataTallies.length === 0) {
            setPaginatedData([]);
            setTotalPages(1);
            setTotalParentRows(0);
            return;
        }

      
        const parentRows = rowDataTallies.filter(item => item.isParent);
        const totalParents = parentRows.length;
        setTotalParentRows(totalParents);

      
        const newTotalPages = Math.ceil(totalParents / pageSize);
        setTotalPages(newTotalPages);

      
        let validPage = currentPage;
        if (currentPage > newTotalPages && newTotalPages > 0) {
            validPage = 1;
            setCurrentPage(1);
        } else if (newTotalPages === 0) {
            validPage = 1;
        }

      
        const startIndex = (validPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedParents = parentRows.slice(startIndex, endIndex);

      
        const result = [];
        for (const parent of paginatedParents) {
            result.push(parent);
            
            if (expandedRows.has(parent.jobId) && parent.children && parent.children.length > 0) {
                result.push(...parent.children);
            }
        }

        setPaginatedData(result);
    }, [rowDataTallies, expandedRows, currentPage, pageSize]);

   
    useEffect(() => {
        updatePaginatedData();
    }, [updatePaginatedData]);

    const fetchJobList = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await GetJobListById(clientId);

            if (response.isSuccess && response.data) {
                const transformedData = response.data.map(job => ({
                    jobCode: job.jobcode || "",
                    jobId: job.jobId,
                    batchNumber: "",
                    date: job.date ? formatDate(job.date) : "",
                    dow: "",
                    nonDonors: job.nonDonors?.toString() || "0",
                    donors: job.donors?.toString() || "0",
                    amount: job.amount?.toString() || "0",
                    lastUpdate: job.lastUpdate ? formatDate(job.lastUpdate) : "",
                    originalDate: job.date,
                    isParent: true,
                    parentId: null,
                    children: [],
                    isLoadingChildren: false,
                    hasLoadedChildren: false
                }));

                setRowDataTallies(transformedData);
                setCurrentPage(1);
                setExpandedRows(new Set());
            } else {
                setError("No record found");
            }
        } catch (err) {
            setError(err.message || "Failed to fetch data");
            console.error("Error fetching job list:", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleRowExpand = useCallback(async (jobId, rowData) => {
       
        if (expandedRows.has(jobId)) {
            const newExpandedRows = new Set(expandedRows);
            newExpandedRows.delete(jobId);
            setExpandedRows(newExpandedRows);
            return;
        }

      
        if (rowData.hasLoadedChildren && rowData.children && rowData.children.length > 0) {
            const newExpandedRows = new Set(expandedRows);
            newExpandedRows.add(jobId);
            setExpandedRows(newExpandedRows);
            return;
        }

      
        setRowDataTallies(prevData =>
            prevData.map(item =>
                item.jobId === jobId
                    ? { ...item, isLoadingChildren: true }
                    : item
            )
        );

        try {
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), 10000);
            });

            const fetchPromise = GetTalliesListById(jobId, clientId);
            const talliesResponse = await Promise.race([fetchPromise, timeoutPromise]);

            if (talliesResponse.isSuccess && talliesResponse.data && talliesResponse.data.length > 0) {
                const childRows = talliesResponse.data.map((batchItem, index) => ({
                    jobCode: `↳ ${batchItem.batchNumber || `Batch ${index + 1}`}`,
                    jobId: `${jobId}-child-${index}`,
                    batchNumber: batchItem.batchNumber?.toString() || "",
                    date: batchItem.date ? formatDate(batchItem.date) : "",
                    dow: batchItem.date ? getDayOfWeek(batchItem.date) : "",
                    nonDonors: batchItem.nonDonors?.toString() || "0",
                    donors: batchItem.donors?.toString() || "0",
                    amount: batchItem.amount?.toString() || "0",
                    lastUpdate: batchItem.lastUpdate ? formatDate(batchItem.lastUpdate) : "",
                    isParent: false,
                    parentId: jobId
                }));

                setRowDataTallies(prevData =>
                    prevData.map(item => {
                        if (item.jobId === jobId) {
                            return {
                                ...item,
                                children: childRows,
                                isLoadingChildren: false,
                                hasLoadedChildren: true
                            };
                        }
                        return item;
                    })
                );

                const newExpandedRows = new Set(expandedRows);
                newExpandedRows.add(jobId);
                setExpandedRows(newExpandedRows);
            } else {
                setRowDataTallies(prevData =>
                    prevData.map(item =>
                        item.jobId === jobId
                            ? {
                                ...item,
                                isLoadingChildren: false,
                                hasLoadedChildren: true,
                                children: []
                            }
                            : item
                    )
                );
            }
        } catch (err) {
            console.error("Error fetching tallies details:", err);
            setRowDataTallies(prevData =>
                prevData.map(item =>
                    item.jobId === jobId
                        ? {
                            ...item,
                            isLoadingChildren: false,
                            hasLoadedChildren: false,
                            children: []
                        }
                        : item
                )
            );
        }
    }, [clientId, formatDate, getDayOfWeek, expandedRows]);

    const JobIdCellRenderer = useCallback((params) => {
        const rowData = params.data;
        if (!rowData) return null;

        if (rowData.isParent) {
            const isExpanded = expandedRows.has(rowData.jobId);

            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                        onClick={() => toggleRowExpand(rowData.jobId, rowData)}
                        disabled={rowData.isLoadingChildren}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: rowData.isLoadingChildren ? 'wait' : 'pointer',
                            fontSize: 'inherit',
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Icon
                            name={isExpanded ? "expand-open" : "expand-closed"}
                            size={16}
                            alt={isExpanded ? "Collapse" : "Expand"}
                            className="me-2"
                            style={{
                                opacity: rowData.isLoadingChildren ? 0.5 : 1
                            }}
                        />
                        {rowData.jobCode}
                    </button>
                    {rowData.isLoadingChildren && (
                        <div className="loading-spinner" style={{
                            width: '12px',
                            height: '12px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #3498db',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                    )}
                </div>
            );
        } else {
            return (
                <div style={{ paddingLeft: '20px' }}>
                    {/* {rowData.jobCode} */}
                </div>
            );
        }
    }, [expandedRows, toggleRowExpand]);



    const talliesDefs = useMemo(() => [
        {
            field: 'jobCode',
            headerName: 'Job ID',
            width: 180,
            maxWidth: 200,
            minWidth: 150,
            suppressSizeToFit: true,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: JobIdCellRenderer
        },
        {
            field: 'batchNumber',
            headerName: 'Batch Number',
            minWidth: 280,
            flex: 1.5,
            filter: 'agTextColumnFilter',
            sortable: true,
            cellRenderer: (params) => {
                if (!params.value || params.value.trim() === '') return '';
                return (
                    <span
                       
                        onClick={() =>
                            navigate(`/talliesSection`, {
                                state: {
                                    ...(location.state || {}),
                                    batchNumber: params.value,
                                    clientId: clientId,
                                    clientName: clientName   
                                }
                            })
                        }

                        style={{
                            textDecoration: 'underline',
                            color: '#0D6EFD',
                            fontWeight: 'normal',
                            cursor: 'default'
                        }
                        }>
                        {params.value}
                    </span >
                );
            }
        },
        {
            field: 'date',
            headerName: 'Date',
            comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            minWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true
        },
        {
            field: 'dow',
            headerName: 'DOW',
            minWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true
        },
        {
            field: 'nonDonors',
            headerName: 'Non Donors',
            minWidth: 200,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            valueFormatter: (params) => {
                if (!params.value) return "0";
                return formatNumberWithCommas(params.value);
            },
            cellStyle: { textAlign: 'right' }
        },
        {
            field: 'donors',
            headerName: 'Donors',
            minWidth: 150,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            valueFormatter: (params) => {
                if (!params.value) return "0";
                return formatNumberWithCommas(params.value);
            },
            cellStyle: { textAlign: 'right' }
        },
        {
            field: 'amount',
            headerName: 'Amount',
            minWidth: 200,
            flex: 1,
            filter: 'agTextColumnFilter',
            sortable: true,
            valueFormatter: (params) => {
                if (!params.value) return '$0.00';
                const num = parseFloat(params.value);
                if (isNaN(num)) return params.value;
                return '$' + num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            cellStyle: { textAlign: 'right' }
        },
        {
            field: 'lastUpdate',
            headerName: 'Last Update',
            width: 250,
            minWidth: 250,
            maxWidth: 250,
            suppressSizeToFit: true,
            comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            filter: 'agTextColumnFilter',
            sortable: true
        }
    ], [JobIdCellRenderer, formatNumberWithCommas]);

    const getRowStyle = useCallback((params) => {
        if (params.data && !params.data.isParent) {
            return { backgroundColor: '#f8f9fa' };
        }
        return null;
    }, []);

  
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '500px'
            }}>
                Loading job list...
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-info mb-3" role="alert" >
                {error}
            </div>
        );
    }

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    .ag-row {
                        border-bottom: 1px solid #ddd;
                    }
                    
                    .ag-row:hover {
                        background-color: #f5f5f5 !important;
                    }
                `}
            </style>
            <div>
                <AgGrid
                    rowData={paginatedData}
                    columnDefs={talliesDefs}
                    pagination={false}
                    height="500px"
                    hideExportButton={true}
                    hideSideBar={true}
                    defaultColDef={{
                        resizable: true,
                        filter: true,
                        sortable: true
                    }}
                    getRowStyle={getRowStyle}
                    hideSelectAll={true}
               
                />
              
            </div>
        </>
    );
}

export default React.memo(Tallies);